import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  QBodyLayout,
  QButton,
  QButtonGroup,
  QHeader,
  QNotFound,
  QStack,
  QText,
  QTitle,
  useFeatureFlag,
} from "@qualio/ui-components";
import { FeatureFlags } from "@design-controls/types";
import { CompareViewRoute } from "../../types/router";

export const CompareView: React.FC = () => {
  const { t } = useTranslation();
  const { selectedItemId, baseVersion } =
    useParams<CompareViewRoute>() as CompareViewRoute;
  const [searchParams] = useSearchParams();
  const viewedVersion = searchParams.get("version");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const features = useFeatureFlag();
  const compareEnabled = Boolean(
    features[FeatureFlags.VERSION_TRACELINK_UI] &&
      features[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );

  const onStopComparing = useCallback(() => {
    const elementPath = pathname.slice(0, pathname.indexOf("/compare/"));
    navigate(
      viewedVersion ? `${elementPath}/version/${viewedVersion}` : elementPath,
    );
  }, [navigate, pathname, viewedVersion]);

  if (!compareEnabled) {
    return <QNotFound data-cy="not-found" />;
  }

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QTitle>{selectedItemId}</QTitle>
        <QButtonGroup>
          <QButton
            variant="outline"
            data-cy="stop-comparing"
            onClick={onStopComparing}
          >
            {t("designElementCompare.stopComparing")}
          </QButton>
        </QButtonGroup>
      </QHeader>
      <QStack gap="8px" data-cy="compare-view">
        <QText fontSize="sm" data-cy="compare-base-version">
          {baseVersion}
        </QText>
        <QText fontSize="sm" data-cy="compare-compare-version">
          {viewedVersion ?? "latest"}
        </QText>
      </QStack>
    </QBodyLayout.Default>
  );
};
